import { z } from 'zod';

/** Polices de sous-titres (licence SIL OFL), stockées en TTF dans assets/fonts. */
export const CAPTION_FONTS = [
  { id: 'anton', family: 'Anton', file: 'Anton-Regular.ttf' },
  { id: 'archivo-black', family: 'Archivo Black', file: 'ArchivoBlack-Regular.ttf' },
  { id: 'bebas-neue', family: 'Bebas Neue', file: 'BebasNeue-Regular.ttf' },
  { id: 'poppins', family: 'Poppins ExtraBold', file: 'Poppins-ExtraBold.ttf' },
  { id: 'bangers', family: 'Bangers', file: 'Bangers-Regular.ttf' },
] as const;

export type CaptionFontId = (typeof CAPTION_FONTS)[number]['id'];
const FONT_IDS = CAPTION_FONTS.map((f) => f.id) as [CaptionFontId, ...CaptionFontId[]];

export function captionFont(id: CaptionFontId) {
  return CAPTION_FONTS.find((f) => f.id === id) ?? CAPTION_FONTS[0];
}

const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Couleur au format #RRGGBB');

export const captionStyleSchema = z.object({
  font: z.enum(FONT_IDS).default('anton'),
  /** Taille en pixels pour une vidéo de 1080 px de large (mise à l'échelle sinon). */
  fontSize: z.number().int().min(28).max(160).default(84),
  textColor: hexColor.default('#FFFFFF'),
  highlightColor: hexColor.default('#FFD60A'),
  outlineColor: hexColor.default('#000000'),
  outlineWidth: z.number().min(0).max(12).default(6),
  shadow: z.number().min(0).max(8).default(2),
  uppercase: z.boolean().default(true),
  position: z.enum(['top', 'middle', 'bottom']).default('bottom'),
  /** Décalage vertical depuis le bord, en % de la hauteur. */
  offset: z.number().min(0).max(40).default(22),
  maxWordsPerLine: z.number().int().min(1).max(8).default(3),
  maxLines: z.number().int().min(1).max(3).default(1),
  /** none : texte fixe ; highlight : mot prononcé coloré ; pop : mot prononcé agrandi ; reveal : mots qui apparaissent un à un. */
  animation: z.enum(['none', 'highlight', 'pop', 'reveal']).default('highlight'),
});

export type CaptionStyle = z.infer<typeof captionStyleSchema>;

export const CAPTION_PRESETS: { id: string; name: string; style: CaptionStyle }[] = [
  {
    id: 'punch',
    name: 'Punch',
    style: captionStyleSchema.parse({}),
  },
  {
    id: 'pop',
    name: 'Pop',
    style: captionStyleSchema.parse({
      font: 'poppins',
      fontSize: 76,
      highlightColor: '#3DF5A6',
      animation: 'pop',
      maxWordsPerLine: 2,
    }),
  },
  {
    id: 'comic',
    name: 'Comic',
    style: captionStyleSchema.parse({
      font: 'bangers',
      fontSize: 92,
      highlightColor: '#FF5A36',
      outlineWidth: 7,
      animation: 'highlight',
    }),
  },
  {
    id: 'reveal',
    name: 'Révélation',
    style: captionStyleSchema.parse({
      font: 'archivo-black',
      fontSize: 68,
      uppercase: false,
      animation: 'reveal',
      maxWordsPerLine: 4,
      maxLines: 2,
      position: 'middle',
      offset: 0,
    }),
  },
  {
    id: 'sober',
    name: 'Sobre',
    style: captionStyleSchema.parse({
      font: 'bebas-neue',
      fontSize: 80,
      outlineWidth: 0,
      shadow: 4,
      animation: 'none',
      maxWordsPerLine: 5,
      maxLines: 2,
    }),
  },
];

export const DEFAULT_CAPTION_STYLE = CAPTION_PRESETS[0]!.style;
